import React, { useState, useRef, useEffect, useCallback } from 'react';
import { formatTimeNodeTitle } from '../../utils/presentationUtils';

const ProgressBar = ({ 
  duration = 300, 
  currentTime, 
  audioOffset = 0, 
  memoryPlay = false,
  presentationTimeNodes = [], // 新增：课件时间节点
  onSeek 
}) => {

  const [isHovering, setIsHovering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hoverPosition, setHoverPosition] = useState(0);
  const [hoverTime, setHoverTime] = useState(0);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [showResumeBubble, setShowResumeBubble] = useState(false);
  const [hoveredPresentationNode, setHoveredPresentationNode] = useState(null); // 新增：用于存储悬停的课件节点

  const progressBarRef = useRef(null);
  const progressContainerRef = useRef(null);

  // 检测是否为触摸设备
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  useEffect(() => { 
    setShowResumeBubble(memoryPlay);
  }, [memoryPlay]);

  // 格式化时间显示 (秒 -> 分:秒)
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // 计算点击/拖动位置对应的时间
  const calculateTimeFromPosition = useCallback((clientX) => {
    if (!progressContainerRef.current) return 0;
    
    const rect = progressContainerRef.current.getBoundingClientRect();
    const position = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return position * duration;
  }, [duration]);

  // 查找悬停位置附近的课件节点
  const findNearbyPresentationNode = useCallback((hoverTime) => {
    if (!presentationTimeNodes || presentationTimeNodes.length === 0) return null;
    
    // 将hoverTime从秒转换为毫秒进行比较
    const hoverTimeMs = hoverTime * 1000;
    
    // 查找最近的课件节点（在1秒范围内的）
    let closestNode = null;
    let minDistance = 1000; // 1秒范围
    
    presentationTimeNodes.forEach(node => {
      const distance = Math.abs(node.time * 1000 - hoverTimeMs);
      if (distance < minDistance) {
        minDistance = distance;
        closestNode = node;
      }
    });
    
    // 只有在足够接近时才返回节点
    return minDistance <= 500 ? closestNode : null; // 0.5秒范围
  }, [presentationTimeNodes]);

  // 处理鼠标移动事件
  const handleMouseMove = useCallback((e) => {
    if (!progressContainerRef.current || isTouchDevice) return;
    
    const rect = progressContainerRef.current.getBoundingClientRect();
    const position = (e.clientX - rect.left) / rect.width;
    setHoverPosition(position * 100);
    const newHoverTime = calculateTimeFromPosition(e.clientX);
    setHoverTime(newHoverTime);
    
    // 查找附近的课件节点
    const nearbyNode = findNearbyPresentationNode(newHoverTime);
    setHoveredPresentationNode(nearbyNode);

  }, [calculateTimeFromPosition, isTouchDevice, findNearbyPresentationNode]);

  // 处理触摸移动事件
  const handleTouchMove = useCallback((e) => {
    if (!progressContainerRef.current || !isTouchDevice) return;
    
    const touch = e.touches[0];
    const rect = progressContainerRef.current.getBoundingClientRect();
    const position = (touch.clientX - rect.left) / rect.width;
    setHoverPosition(position * 100);
    setHoverTime(calculateTimeFromPosition(touch.clientX));
    
  }, [calculateTimeFromPosition, isTouchDevice]);

  // 处理进度条点击
  const handleClick = useCallback((e) => {
    if (isDragging || isTouchDevice) return;
    
    const clickTime = calculateTimeFromPosition(e.clientX);
    onSeek(clickTime);
  }, [isDragging, calculateTimeFromPosition, onSeek, isTouchDevice]);

  // 处理触摸点击
  const handleTouchStart = useCallback((e) => {
    if (!isTouchDevice) return;
    
    const touch = e.touches[0];
    const clickTime = calculateTimeFromPosition(touch.clientX);
    onSeek(clickTime);
  }, [calculateTimeFromPosition, onSeek, isTouchDevice]);

  // 开始拖动
  const startDrag = useCallback((e) => {
    setIsDragging(true);
    
    // 为鼠标/触摸移动和结束添加事件监听
    const handleMove = (moveEvent) => {
      let clientX;
      if (moveEvent.touches) {
        clientX = moveEvent.touches[0].clientX;
        moveEvent.preventDefault();
      } else {
        clientX = moveEvent.clientX;
      }
      
      const newTime = calculateTimeFromPosition(clientX);
      setHoverTime(newTime);
      onSeek(newTime);
    };
    
    const handleEnd = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchend', handleEnd);
    };
    
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchend', handleEnd);
    
    e.preventDefault();
  }, [calculateTimeFromPosition, onSeek]);

  // 进度百分比 - 修复除以0的情况
  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div style={{ width: '100%', marginTop: '10px' }}>
      <div
        ref={progressContainerRef}
        style={{
          position: 'relative',
          height: '8px',
          backgroundColor: '#e0e0e0',
          borderRadius: '6px',
          cursor: 'pointer',
          marginBottom: '10px',
          touchAction: 'none'
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          setIsHovering(false);
          setHoveredPresentationNode(null); // 清除悬停的课件节点
        }}
        onClick={handleClick}
        onTouchMove={handleTouchMove}
        onTouchStart={handleTouchStart}
      >
        {/* 背景轨道 */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: '100%',
          borderRadius: '6px',
          backgroundColor: '#e0e0e0'
        }}></div>
        
        {/* 已播放进度 */}
        <div 
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            borderRadius: '6px',
            backgroundColor: '#fb4643',
            width: `${progressPercentage}%`
          }}
        ></div>
        
        {/* 课件时间节点标记 */}
        {presentationTimeNodes && presentationTimeNodes.map((node, index) => {
          // 只显示在播放范围内的节点
          // node.time是以秒为单位的（精确到毫秒的小数），duration也是以秒为单位
          if (node.time >= 0 && node.time <= duration && duration > 0) {
            const position = (node.time / duration) * 100;
            return (
              <div
                key={`presentation-${index}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: `${position}%`,
                  height: '100%',
                  width: '2px', // 默认宽度2px
                  backgroundColor: 'coral', // 保留coral颜色
                  zIndex: 1,
                  pointerEvents: 'auto', // 允许点击事件
                  transform: 'translateX(-50%)',
                  cursor: 'pointer', // 显示手型光标
                  borderRadius: '1px', // 添加轻微圆角
                  transition: 'all 0.2s ease', // 添加过渡效果
                  boxShadow: hoveredPresentationNode === node ? '0 0 0 2px rgba(255, 165, 0, 0.7)' : 'none', // 悬停时添加光晕效果
                }}
                title={formatTimeNodeTitle(node)}
                onClick={(e) => {
                  // 阻止事件冒泡，防止触发进度条的点击事件
                  e.stopPropagation();
                  // 添加视觉反馈
                  e.target.style.backgroundColor = '#ff8c00'; // 更亮的橙色
                  e.target.style.transform = 'translateX(-50%) scale(1.2)'; // 放大效果
                  // 短暂延迟后恢复原样
                  setTimeout(() => {
                    if (e.target) {
                      e.target.style.backgroundColor = 'coral';
                      e.target.style.transform = 'translateX(-50%)';
                    }
                  }, 200);
                  onSeek(node.time); // 添加点击事件处理
                }}
                onMouseEnter={(e) => {
                  // 悬停时增加宽度和点击区域
                  e.target.style.width = '6px';
                  e.target.style.boxShadow = '0 0 0 2px rgba(255, 165, 0, 0.7)';
                  // 增加高度以扩大点击区域
                  e.target.style.height = '120%';
                  e.target.style.top = '-10%';
                  setHoveredPresentationNode(node); // 设置悬停的课件节点
                }}
                onMouseLeave={(e) => {
                  // 离开时恢复原始样式
                  e.target.style.width = '2px';
                  e.target.style.boxShadow = hoveredPresentationNode === node ? '0 0 0 2px rgba(255, 165, 0, 0.7)' : 'none';
                  // 恢复原始高度
                  e.target.style.height = '100%';
                  e.target.style.top = '0';
                  if (hoveredPresentationNode === node) {
                    setHoveredPresentationNode(null); // 清除悬停的课件节点
                  }
                }}
              ></div>
            );
          }
          return null;
        })}
        
        {/* 课件节点点击区域扩展 */}
        {presentationTimeNodes && presentationTimeNodes.map((node, index) => {
          // 只显示在播放范围内的节点
          if (node.time >= 0 && node.time <= duration && duration > 0) {
            const position = (node.time / duration) * 100;
            return (
              <div
                key={`presentation-click-area-${index}`}
                style={{
                  position: 'absolute',
                  top: '-20%',
                  left: `${position}%`,
                  height: '140%',
                  width: '12px',
                  backgroundColor: 'transparent',
                  zIndex: 0,
                  pointerEvents: 'auto',
                  transform: 'translateX(-50%)',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  // 阻止事件冒泡，防止触发进度条的点击事件
                  e.stopPropagation();
                  onSeek(node.time);
                }}
              ></div>
            );
          }
          return null;
        })}
        
        {/* Audio Offset 标记 */}
        {audioOffset > 0 && duration > 0 && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: `${(audioOffset / duration) * 100}%`,
              height: '100%',
              width: '2px',
              backgroundColor: 'burlywood', // 标记线
              zIndex: 1,
              pointerEvents: 'none',
              transform: 'translateX(-50%)',
            }}
            title={`Audio starts at ${formatTime(audioOffset)}`}
          ></div>
        )}

        {/* 拖动滑块 */}
        <div 
          ref={progressBarRef}
          style={{ 
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: isDragging ? '24px' : '20px',
            height: isDragging ? '24px' : '20px',
            borderRadius: '50%',
            backgroundColor: isDragging ? '#ff3742' : '#fb4643',
            cursor: 'pointer',
            left: `${progressPercentage}%`,
            boxShadow: isDragging ? '0 0 5px rgba(255, 71, 87, 0.7)' : 'none',
          }}
          onMouseDown={startDrag}
          onTouchStart={startDrag}
        ></div>

        {/* 记忆播放：已跳转提示气泡 */}
        {showResumeBubble && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(50% - 42px)', // 在滑块上方约 42px
              left: `${progressPercentage}%`,
              backgroundColor: 'white',
              color: 'black',
              fontSize: '12px',
              padding: '5px 5px',
              borderRadius: '10px',
              whiteSpace: 'nowrap',
              zIndex: 4,
              opacity: 0.95,
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
              pointerEvents: 'none',
              animation: 'fadeInOut 3s ease-in-out', // 可选动画
            }}
          >
            已跳转到上次播放位置
          </div>
        )}
        
        {/* Hover时显示的时间提示 */}
        {isHovering && !isTouchDevice && (
          <div 
            style={{
              position: 'absolute',
              bottom: '25px',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              padding: '5px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              left: `${hoverPosition}%`
            }}
          >
            {formatTime(hoverTime)}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressBar;